// Global Instructions Rule Applied!
// Read shapes of the statement run form (spec A3.14 stage 5):
//   view__investor_statement_run   the run, its funds with where each
//                                  stands, and the statements stored for them
//   stmt__run_fund_options         what "Add funds" offers
//   stmt__run_fund_changes         "out of date" for one fund of a run
//   view__fund_statement_runs      the runs a fund or a consolidated fund is
//   / view__fund_group_...         in (its Statements tab)
// The backend twin is investor_statement_run_record.py; both are strict, so a
// key a read starts to emit is added to both first.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'
import { StatementCheckSchema } from './investor_statement_checks.ts'

/** Where a fund of a run stands (stmt__run_fund_states). */
export const RunFundState = {
  notGenerated: 'not_generated',
  queued: 'queued',
  generating: 'generating',
  stalled: 'stalled',
  blocked: 'blocked',
  failed: 'failed',
  generated: 'generated'
} as const

/** cfg.investor_statement_statuses codes. */
export const StatementStatusCode = {
  pending: 'pending',
  generated: 'generated',
  failed: 'failed',
  superseded: 'superseded'
} as const

/** What a stored statement is about. */
export const StatementScope = {
  // the Fund Financial Statements or the fund report
  entity: 'entity',
  // a commitment of a fund
  commitment: 'commitment',
  // a consolidated commitment
  subscription: 'subscription',
  // its account in one legal entity
  pocket: 'pocket'
} as const

/** What a stored document is: a capital account statement, the Fund
 *  Financial Statements (the pack) or the Quarterly Fund Report
 *  (docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md d.1). */
export const StatementKind = {
  statement: 'statement',
  pack: 'pack',
  report: 'report'
} as const

/** Where a run-level per-account document (the investor performance deck,
 *  docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md d.1, PR 4) stands
 *  (stmt__run_account_document_states). */
export const AccountDocumentState = {
  pending: 'pending',
  stalled: 'stalled',
  generated: 'generated',
  failed: 'failed'
} as const

/** cfg.statement_document_groups codes: the groups the run's documents fall
 *  into, each generated, issued and sent on its own. */
export const DocumentGroupCode = {
  reports: 'reports',
  statements: 'statements'
} as const

/** A live document group, as the run read lists them (cfg). */
const DocumentGroupSchema = z
  .object({
    statement_document_groups__id: z.number(),
    statement_document_groups__code: z.string(),
    statement_document_groups__label: z.string(),
    statement_document_groups__display_order: z.number().nullish(),
    email_design_template_id: z.string().nullish()
  })
  .strict()

export type DocumentGroup = z.infer<typeof DocumentGroupSchema>

const RunFundCountsSchema = z
  .object({
    total: z.number(),
    generated: z.number(),
    pending: z.number(),
    failed: z.number()
  })
  .strict()

/** Where a fund of a run stands in one document group. */
const RunFundGroupSchema = z
  .object({
    statement_document_groups__code: z.string(),
    statement_document_groups__label: z.string(),
    statement_document_groups__display_order: z.number().nullish(),
    // the fund produces documents of the group (the reports group needs
    // Include Fund Report on its statement settings)
    prog__enabled: z.boolean(),
    prog__counts: RunFundCountsSchema,
    prog__file_size_bytes: z.number(),
    prog__issued_count: z.number(),
    prog__issued_at: z.string().nullish(),
    prog__revised_unissued_count: z.number(),
    prog__sent_count: z.number(),
    // why the group cannot be sent; null when it can
    prog__send_blocked_reason: z.string().nullish()
  })
  .strict()

export type InvestorStatementRunFundGroup = z.infer<typeof RunFundGroupSchema>

const RunSchema = z
  .object({
    investor_statement_runs__id: z.string(),
    investor_statement_runs__name: z.string(),
    investor_statement_runs__period_end: z.string(),
    investor_statement_runs__period_end_fmt: z.string(),
    prog__period_label: z.string(),
    investor_statement_runs__statement_date: z.string(),
    investor_statement_runs__statement_date_fmt: z.string(),
    investor_statement_runs__notes: z.string().nullish(),
    investor_statement_runs__date_created: z.string().nullish(),
    // the period end is fixed while the run holds funds
    prog__has_funds: z.boolean(),
    // the statement date is fixed once a statement of the run is issued
    prog__has_issued: z.boolean(),
    // the reader may undo an issue and remove what was issued
    prog__is_app_admin: z.boolean(),
    // why "Send statements" is not available at all (stage 6); null when a
    // fund of the run can be sent
    prog__send_blocked_reason: z.string().nullish(),
    // the live document groups (reports, statements), in display order
    prog__document_groups: z.array(DocumentGroupSchema),
    // the account-scope document type (the investor performance deck, one
    // per investor account across every fund of the run); null when none
    // is configured
    prog__account_document_type: z
      .object({
        investor_statement_types__code: z.string(),
        investor_statement_types__label: z.string(),
        investor_statement_types__short_label: z.string(),
        statement_document_groups__code: z.string().nullish()
      })
      .strict()
      .nullish()
  })
  .strict()

const RunFundSchema = z
  .object({
    investor_statement_run_funds__id: z.string(),
    prog__kind: z.enum(['fund', 'fund_group']),
    funds__id: z.string().nullish(),
    fund_groups__id: z.string().nullish(),
    prog__name: z.string(),
    investor_statement_run_funds__queued_at: z.string().nullish(),
    investor_statement_run_funds__generation_started_at: z.string().nullish(),
    investor_statement_run_funds__generation_finished_at: z.string().nullish(),
    // why nothing was generated, as a sentence
    investor_statement_run_funds__error_message: z.string().nullish(),
    prog__state: z.enum(['not_generated', 'queued', 'generating', 'stalled', 'blocked', 'failed', 'generated']),
    // the gate's answer at the last generation
    prog__blocking: z.array(StatementCheckSchema),
    prog__warnings: z.array(StatementCheckSchema),
    // the run-only checks (legal name, notice sender) as they stand now
    prog__problems: z.array(StatementCheckSchema),
    prog__counts: RunFundCountsSchema,
    prog__file_size_bytes: z.number(),
    // its current statements marked as issued, and when (the latest)
    prog__issued_count: z.number(),
    prog__issued_at: z.string().nullish(),
    // current statements of a later version that are not issued yet
    prog__revised_unissued_count: z.number(),
    // its current investor statements that reached their account (stage 6)
    prog__sent_count: z.number(),
    // why this fund cannot be sent; null when it can
    prog__send_blocked_reason: z.string().nullish(),
    // where the fund stands per document group
    prog__groups: z.array(RunFundGroupSchema)
  })
  .strict()

export type InvestorStatementRunFund = z.infer<typeof RunFundSchema>

const RunStatementSchema = z
  .object({
    investor_statements__id: z.string(),
    investor_statement_run_funds__id: z.string(),
    investor_statement_types__code: z.string(),
    investor_statement_types__label: z.string(),
    investor_statement_types__short_label: z.string(),
    // the document group of its type; null for a type in no group
    statement_document_groups__code: z.string().nullish(),
    // statement | pack | report
    prog__kind: z.enum(['statement', 'pack', 'report']),
    prog__scope: z.enum(['entity', 'commitment', 'subscription', 'pocket']),
    prog__run_fund_name: z.string(),
    // the legal entity of an entity statement, else the run fund
    prog__entity_name: z.string(),
    fund_commitments__id: z.string().nullish(),
    fund_group_commitments__id: z.string().nullish(),
    prog__member_fund_id: z.string().nullish(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    investor_statements__version: z.number(),
    investor_statements__statement_date: z.string().nullish(),
    investor_statements__statement_date_fmt: z.string().nullish(),
    investor_statements__states_carry: z.boolean(),
    investor_statements__issued_at: z.string().nullish(),
    investor_statements__page_count: z.number().nullish(),
    investor_statements__error_message: z.string().nullish(),
    investor_statements__generated_at: z.string().nullish(),
    investor_statements__date_created: z.string().nullish(),
    investor_statement_statuses__code: z.string().nullish(),
    investor_statement_statuses__label: z.string().nullish(),
    investor_statement_statuses__tag_color: z.string().nullish(),
    file_urls_investor_statements__id: z.string().nullish(),
    file_urls_investor_statements__file_name: z.string().nullish(),
    file_urls_investor_statements__file_url: z.string().nullish(),
    file_urls_investor_statements__file_size_bytes: z.number().nullish(),
    // still Generating while the run has shown no activity for the limit
    prog__is_stalled: z.boolean(),
    prog__superseded_count: z.number(),
    // the statement of the fund's carry row (the commitment that receives the
    // carried interest and has nothing committed): generated, never sent
    prog__is_carry_row: z.boolean(),
    // sending (stage 6): when the statement reached its account, where it
    // stands in a statement email, and that email
    prog__sent_at: z.string().nullish(),
    prog__send_state: z.enum(['sent', 'draft', 'queued', 'sending', 'failed']).nullish(),
    email_campaigns__id: z.string().nullish(),
    email_campaigns__name: z.string().nullish()
  })
  .strict()

/** Where a stored statement stands in a statement email. */
export const StatementSendState = {
  sent: 'sent',
  draft: 'draft',
  queued: 'queued',
  sending: 'sending',
  failed: 'failed'
} as const

export type InvestorStatementRow = z.infer<typeof RunStatementSchema>

/** A statement email of the run (investor_statement_email_campaigns). */
const RunCampaignSchema = z
  .object({
    email_campaigns__id: z.string(),
    email_campaigns__name: z.string().nullish(),
    email_campaigns__status: z.string(),
    email_campaigns__sent_count: z.number().nullish(),
    email_campaigns__recipient_count: z.number().nullish(),
    email_campaigns__completed_at: z.string().nullish(),
    // the funds of the run the email was created for
    investor_statement_email_campaigns__run_fund_ids: z.array(z.string()),
    // the document groups the email was created for
    investor_statement_email_campaigns__document_group_codes: z.array(z.string()),
    // the part of its set this email is (docs/QUARTERLY_REPORT_DISTRIBUTION_
    // DESIGN.md d.3): part_no of part_count, the siblings sharing
    // part_set_id; 1 of 1 for an email that stands alone
    investor_statement_email_campaigns__part_no: z.number(),
    investor_statement_email_campaigns__part_count: z.number(),
    investor_statement_email_campaigns__part_set_id: z.string()
  })
  .strict()

export type InvestorStatementRunCampaign = z.infer<typeof RunCampaignSchema>

/** The current investor performance deck of an account of the run: a
 *  run-level document (investor_statement_account_documents), generated
 *  with the fund reports, issued with them and sent once per account as the
 *  account's first attachment. */
const RunAccountDocumentSchema = z
  .object({
    investor_statement_account_documents__id: z.string(),
    accounts__id: z.string(),
    accounts__name: z.string(),
    investor_statement_types__code: z.string(),
    investor_statement_types__label: z.string(),
    investor_statement_types__short_label: z.string(),
    statement_document_groups__code: z.string().nullish(),
    document_report_code: z.string(),
    investor_statement_account_documents__version: z.number(),
    investor_statement_account_documents__issued_at: z.string().nullish(),
    investor_statement_account_documents__page_count: z.number().nullish(),
    investor_statement_account_documents__error_message: z.string().nullish(),
    investor_statement_account_documents__generated_at: z.string().nullish(),
    investor_statement_account_documents__date_created: z.string().nullish(),
    investor_statement_statuses__code: z.string().nullish(),
    investor_statement_statuses__label: z.string().nullish(),
    investor_statement_statuses__tag_color: z.string().nullish(),
    file_urls_investor_statement_account_documents__id: z.string().nullish(),
    file_urls_investor_statement_account_documents__file_name: z.string().nullish(),
    file_urls_investor_statement_account_documents__file_url: z.string().nullish(),
    file_urls_investor_statement_account_documents__file_size_bytes: z.number().nullish(),
    // pending | stalled | generated | failed
    prog__state: z.enum(['pending', 'stalled', 'generated', 'failed']),
    prog__is_stalled: z.boolean(),
    prog__superseded_count: z.number(),
    // the LP's Period Highlights set, or the report the type prints, moved
    // since the deck was generated
    prog__is_stale: z.boolean(),
    prog__changed_parts: z.array(z.string()),
    prog__sent_at: z.string().nullish(),
    prog__send_state: z.enum(['sent', 'draft', 'queued', 'sending', 'failed']).nullish(),
    email_campaigns__id: z.string().nullish(),
    email_campaigns__name: z.string().nullish()
  })
  .strict()

export type InvestorStatementAccountDocument = z.infer<typeof RunAccountDocumentSchema>

const RunAccountDocumentSummarySchema = z
  .object({
    total: z.number(),
    generated: z.number(),
    pending: z.number(),
    stalled: z.number(),
    failed: z.number(),
    issued_count: z.number(),
    issued_at: z.string().nullish(),
    revised_unissued_count: z.number(),
    sent_count: z.number(),
    file_size_bytes: z.number()
  })
  .strict()

export type InvestorStatementAccountDocumentSummary = z.infer<typeof RunAccountDocumentSummarySchema>

export const InvestorStatementRunSchema = z
  .object({
    run: RunSchema,
    funds: z.array(RunFundSchema),
    statements: z.array(RunStatementSchema),
    // the statement emails of the run, newest first (stage 6)
    campaigns: z.array(RunCampaignSchema),
    prog__summary: z
      .object({
        funds: z.number(),
        statements: z.number(),
        generated: z.number(),
        pending: z.number(),
        stalled: z.number(),
        failed: z.number(),
        file_size_bytes: z.number()
      })
      .strict(),
    // the run-level per-account documents (the performance decks), by
    // account, and what they add up to
    account_documents: z.array(RunAccountDocumentSchema),
    prog__account_document_summary: RunAccountDocumentSummarySchema,
    // something is queued or being generated (a fund, or a deck): the form
    // keeps reading
    prog__is_active: z.boolean()
  })
  .strict()

export type InvestorStatementRun = z.infer<typeof InvestorStatementRunSchema>

const RunFundOptionSchema = z
  .object({
    prog__kind: z.enum(['fund', 'fund_group']),
    value: z.string(),
    label: z.string(),
    // why it cannot join the run; null when it can
    prog__disabled_reason: z.string().nullish(),
    prog__problems: z.array(StatementCheckSchema)
  })
  .strict()

export type InvestorStatementRunFundOption = z.infer<typeof RunFundOptionSchema>

export const InvestorStatementRunFundOptionsSchema = z
  .object({
    investor_statement_runs__id: z.string(),
    options: z.array(RunFundOptionSchema)
  })
  .strict()

export type InvestorStatementRunFundOptions = z.infer<typeof InvestorStatementRunFundOptionsSchema>

/** stmt__run_fund_changes: which stored statements of one fund of a run
 *  differ from the books and settings as they are now. */
export const RunFundChangesSchema = z
  .object({
    // 'unavailable': the fund's statement data cannot be read now
    status: z.enum(['success', 'unavailable']),
    status_message: z.string().nullish(),
    investor_statement_run_funds__id: z.string(),
    statements: z.array(
      z
        .object({
          investor_statements__id: z.string(),
          prog__is_stale: z.boolean(),
          // the parts of the snapshot that differ (entity, settings,
          // investor, balance_sheet, operations, investments, partners)
          prog__changed_parts: z.array(z.string())
        })
        .strict()
    ),
    prog__stale_count: z.number(),
    // subjects of the live read that have no statement
    prog__missing_count: z.number()
  })
  .strict()

export type RunFundChanges = z.infer<typeof RunFundChangesSchema>

const FundStatementRunSchema = z
  .object({
    investor_statement_runs__id: z.string(),
    investor_statement_runs__name: z.string(),
    investor_statement_runs__period_end: z.string(),
    prog__period_label: z.string(),
    investor_statement_run_funds__id: z.string(),
    // the consolidated fund the statements are made with, for a legal entity
    prog__via_name: z.string().nullish(),
    prog__state: z.enum(['not_generated', 'queued', 'generating', 'stalled', 'blocked', 'failed', 'generated']),
    prog__counts: z
      .object({ total: z.number(), generated: z.number(), pending: z.number(), failed: z.number() })
      .strict(),
    prog__issued_count: z.number(),
    prog__issued_at: z.string().nullish(),
    prog__revised_unissued_count: z.number()
  })
  .strict()

export type FundStatementRun = z.infer<typeof FundStatementRunSchema>

export const FundStatementRunsSchema = z
  .object({
    funds__id: z.string().nullish(),
    fund_groups__id: z.string().nullish(),
    runs: z.array(FundStatementRunSchema)
  })
  .strict()

export type FundStatementRuns = z.infer<typeof FundStatementRunsSchema>

/** What a run is created from, and what one PATCH of its Details changes. */
export interface InvestorStatementRunSavePayload {
  name?: string
  period_end?: string
  statement_date?: string
  notes?: string | null
}

export function validateInvestorStatementRun(data: unknown): InvestorStatementRun {
  try {
    return validateWithZodSchema(InvestorStatementRunSchema, data, 'investor statement run')
  } catch (error) {
    throw new Error(`Investor statement run validation failed: ${error}`)
  }
}

export function validateInvestorStatementRunFundOptions(data: unknown): InvestorStatementRunFundOptions {
  try {
    return validateWithZodSchema(InvestorStatementRunFundOptionsSchema, data, 'investor statement run fund options')
  } catch (error) {
    throw new Error(`Investor statement run fund options validation failed: ${error}`)
  }
}

export function validateRunFundChanges(data: unknown): RunFundChanges {
  try {
    return validateWithZodSchema(RunFundChangesSchema, data, 'investor statement run fund changes')
  } catch (error) {
    throw new Error(`Investor statement run fund changes validation failed: ${error}`)
  }
}

// The agent-written company short descriptions the run's stored fund reports
// and performance decks printed that still need a person, per document id
// (view__investor_statement_run_description_warnings; 2026-11-03):
// unreviewed = not signed off yet; changed = the company's text is no longer
// the one printed (generate the document again).
export const RunDescriptionWarningSchema = z
  .object({
    accounts__id: z.string(),
    accounts__name: z.string().nullish(),
    prog__state: z.enum(['unreviewed', 'changed'])
  })
  .strict()
export type RunDescriptionWarning = z.infer<typeof RunDescriptionWarningSchema>

export const RunDescriptionWarningsSchema = z
  .object({
    statements: z.record(z.string(), z.array(RunDescriptionWarningSchema)),
    account_documents: z.record(z.string(), z.array(RunDescriptionWarningSchema))
  })
  .strict()
export type RunDescriptionWarnings = z.infer<typeof RunDescriptionWarningsSchema>

export function validateRunDescriptionWarnings(data: unknown): RunDescriptionWarnings {
  try {
    return validateWithZodSchema(RunDescriptionWarningsSchema, data, 'run description warnings')
  } catch (error) {
    throw new Error(`Run description warnings validation failed: ${error}`)
  }
}

export function validateFundStatementRuns(data: unknown): FundStatementRuns {
  try {
    return validateWithZodSchema(FundStatementRunsSchema, data, 'fund statement runs')
  } catch (error) {
    throw new Error(`Fund statement runs validation failed: ${error}`)
  }
}
